import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { CONSENT_EVENT, hasConsentDecision } from '../utils/consent';
import AppCtaLink from './AppCtaLink';
import '../styles/AppDownloadPopup.scss';

const SESSION_KEY = 'aipsyhelp_home_app_popup_v1';
const SHOW_DELAY_MS = 1400;
// Auto-prompt on home visit is off for now — re-enable later by setting true.
const AUTO_SHOW_APP_POPUP = false;

const AppDownloadPopup = () => {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const [visible, setVisible] = useState(false);

  const markSeenAndClose = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (_) {
      // ignore storage errors
    }
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!AUTO_SHOW_APP_POPUP) return undefined;

    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (_) {
      alreadySeen = false;
    }

    if (alreadySeen) return undefined;

    const maybeShow = () => {
      if (!hasConsentDecision()) return;
      window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };

    if (hasConsentDecision()) {
      const timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      return () => window.clearTimeout(timer);
    }

    window.addEventListener(CONSENT_EVENT, maybeShow);
    return () => window.removeEventListener(CONSENT_EVENT, maybeShow);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        markSeenAndClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [visible, markSeenAndClose]);

  const highlights = t('home.app_download_popup.highlights', { returnObjects: true });
  const highlightItems = Array.isArray(highlights) ? highlights : [];

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {visible ? (
        <motion.div
          className="app-download-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
        >
          <button
            type="button"
            className="app-download-popup__backdrop"
            aria-label={t('home.app_download_popup.close')}
            onClick={markSeenAndClose}
          />

          <motion.div
            className="app-download-popup__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              className="app-download-popup__close"
              onClick={markSeenAndClose}
              aria-label={t('home.app_download_popup.close')}
            >
              <FaTimes aria-hidden="true" />
            </button>

            <div className="app-download-popup__visual" aria-hidden="true">
              <div className="app-download-popup__visual-glow" />
              <div className="app-download-popup__device">
                <div className="app-download-popup__device-notch" />
                <div className="app-download-popup__device-screen">
                  <span className="app-download-popup__brand">AI PSY HELP</span>
                  <p>{t('home.app_download_popup.device_caption')}</p>
                  <ul>
                    <li />
                    <li />
                    <li />
                  </ul>
                </div>
              </div>
            </div>

            <div className="app-download-popup__content">
              <span className="app-download-popup__kicker">
                {t('home.app_download_popup.kicker')}
              </span>
              <h2 id={titleId}>{t('home.app_download_popup.title')}</h2>
              <p id={descId} className="app-download-popup__lead">
                {t('home.app_download_popup.description')}
              </p>

              {highlightItems.length > 0 ? (
                <ul className="app-download-popup__highlights">
                  {highlightItems.map((item) => (
                    <li key={item}>
                      <FaCheck aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="app-download-popup__stores">
                <AppCtaLink
                  platform="ios"
                  placement="home_app_popup"
                  className="app-download-popup__store"
                  aria-label={t('home.app_download_popup.apple')}
                  onClick={markSeenAndClose}
                >
                  <img
                    src="/images/apple_en.svg"
                    alt={t('home.app_download_popup.apple')}
                  />
                </AppCtaLink>

                <AppCtaLink
                  platform="android"
                  placement="home_app_popup"
                  className="app-download-popup__store"
                  aria-label={t('home.app_download_popup.google')}
                  onClick={markSeenAndClose}
                >
                  <img
                    src="/images/google_en.svg"
                    alt={t('home.app_download_popup.google')}
                  />
                </AppCtaLink>
              </div>

              <button
                type="button"
                className="app-download-popup__web"
                onClick={markSeenAndClose}
              >
                {t('home.app_download_popup.continue_web')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default AppDownloadPopup;
